import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  SlidersHorizontal,
  Cable,
  ScrollText,
  ShieldHalf,
  ShieldCheck,
  Fingerprint,
  BellRing,
  HardDrive,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/sso-dashboard", label: "SSO Dashboard", icon: Fingerprint },
  { to: "/privacy", label: "Privacy Requests", icon: ShieldCheck },
  { to: "/push", label: "Push Delivery", icon: BellRing },
  { to: "/storage", label: "Blob Storage", icon: HardDrive },
  { to: "/configuration", label: "Configuration", icon: SlidersHorizontal },
  { to: "/integrations", label: "Integrations", icon: Cable },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
];

const TITLES = {
  "/": "System Overview",
  "/sso-dashboard": "SSO Dashboard",
  "/privacy": "Privacy Requests",
  "/push": "Push Delivery",
  "/storage": "Blob Storage",
  "/configuration": "Configuration Editor",
  "/integrations": "Connected Services",
  "/audit": "Audit Log",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-[240px] shrink-0 border-r border-border flex flex-col fixed h-screen bg-[#0a0a0a]">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <div className="w-8 h-8 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center">
            <ShieldHalf className="w-4 h-4 text-primary" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-tight">Precipice Forge</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              Security · Privacy
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1" data-testid="sidebar-nav">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-primary/10 text-white border border-primary/30"
                    : "text-muted-foreground hover:text-white hover:bg-white/[0.03] border border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="px-2 py-2 mb-1">
            <div className="text-xs text-white truncate">{user?.name || "Admin"}</div>
            <div className="font-mono text-[10px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-[240px] flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-border flex items-center justify-between px-8 sticky top-0 bg-background/90 backdrop-blur z-10">
          <div>
            <h1 className="text-lg font-medium tracking-tight" data-testid="page-title">
              {TITLES[location.pathname] || "Precipice Forge"}
            </h1>
          </div>
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-dot" />
            forge.precipice-health.com
          </div>
        </header>
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
